<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item :to="{
              name: 'DashboardHome',
            }">
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item
              :to="{
                name: 'SystemSettingsHome',
              }"
            >全系統管理</b-breadcrumb-item>
            <b-breadcrumb-item active>系統方案列表</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <h4 class="mb-2 mb-xl-0 mr-2 font-weight-bold">系統方案列表</h4>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <div
          class="col-12 col-xl-5 d-flex flex-column flex-xl-row align-items-start align-items-xl-center ml-auto"
        >
          <b-button
            v-if="checkPermission([consts.SUITE_CREATE])"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
            variant="primary"
            @click="handleCreate"
          >
            <i class="fa fa-plus"></i>新增系統方案
          </b-button>
          <b-input-group>
            <b-form-input placeholder="搜尋系統方案名稱或編號" v-model="query.keyword" @keyup.enter="handlePageResetWithoutReload"></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageResetWithoutReload">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>

      <b-table striped hover responsive :items="organizations" :fields="fields" :busy="isLoading">
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="checkPermission([consts.SUITE_PACKAGE_LIST_VIEW])"
            class="ml-2"
            variant="inverse-info"
            :to="{
              name: 'SystemSuitePackageList',
              params: {
                suiteId: data.item.id,
              },
            }"
          >
            <span class="mdi mdi-account-star"></span> 方案套件管理
          </b-button>

          <b-button
            class="ml-2"
            variant="inverse-primary"
            :to="{
              name: 'SuiteShow',
              params: {
                suiteId: data.item.id,
              },
            }"
          ><span class="mdi mdi-eye"></span> 查看</b-button>

          <b-button
            v-if="checkPermission([consts.SUITE_MODIFY])"
            class="ml-2"
            variant="inverse-warning"
            :to="{
              name: 'SuiteEdit',
              params: {
                suiteId: data.item.id,
              },
            }"
          ><span class="mdi mdi-lead-pencil"></span> 編輯</b-button>
          <span :id="`delete-btn-${data.item.id}`" v-if="checkPermission([consts.SUITE_DELETE])">
            <b-button
              class="ml-2"
              variant="inverse-danger"
              @click="handleDelete(data.item.id, data.item.name, data.item.code)"
            ><span class="mdi mdi-delete"></span> 刪除</b-button>
          </span>
        </template>
      </b-table>
      <CustomPagination
        :currentPage="query.page"
        :totalRows="total"
        :perPage="query.per_page"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import suiteApi from "../../../apis/suite";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { paginationMixin } from "@/mixins/pagination";

export default {
  components: { CustomPagination },
  mixins: [ paginationMixin ],
  data() {
    return {
      consts,
      isLoading: false,
      organizations: [],
      fields: [
        {
          key: "name",
          label: "系統方案名稱",
        },
        {
          key: "code",
          label: "系統方案代碼",
        },
        {
          key: "is_enabled",
          label: "狀態",
          formatter: (value) => {
            return value ? '啟用' : '停用'
          },
        },
        {
          key: "actions",
          label: "管理",
        },
      ],
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        keyword: "",
        is_enabled: 1,
      },
      total: 0,
    };
  },
  computed: {
    ...mapGetters("route", ["routeQuery"]),
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrlWithoutReload(this.query, this.$store, this.$router);
      },
    },
    routeQuery() {
      if (!this.initialized) return;
      this.fetchSuites();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    if (this.query.is_enabled === "true" || this.query.is_enabled === "1") {
      this.query.is_enabled = true;
    } else if (this.query.is_enabled === "false" || this.query.is_enabled === "0") {
      this.query.is_enabled = false;
    }
  },
  async mounted() {
    await this.fetchSuites();
    this.initialized = true;
  },
  methods: {
    async fetchSuites() {
      try {
        this.isLoading = true;
        let params = {...this.query}
        const { data } = await suiteApi.getSuites(params);
        this.total = data.meta.total;
        this.organizations = data.data;
      } catch (error) {
        console.log("");
      }
      this.isLoading = false;
    },
    async handleDelete(suiteId, name, code) {
      this.$swal
        .fire({
          type: "warning",
          title: "你確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>系統方案名稱：${name}</div>
                <div>系統方案編號：${code}</div>
              </div>
              <div>刪除後，相關資料將無法回復，<br/>請您確認！</div>
            </div>`,
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            try {
              this.isLoading = true;
              await suiteApi.deleteSuite(suiteId);

              this.$swal
                .fire({
                  type: "success",
                  text: `${name} 已成功刪除`,
                })
                .then(async () => {
                  // 重新載入資料
                  await this.fetchSuites();
                });
            } catch (error) {
              console.log("刪除失敗:", error);
              // 顯示錯誤訊息
              await this.$swal.fire({
                type: "error",
                title: "刪除失敗",
                text: "刪除系統方案時發生錯誤，請稍後再試",
                confirmButtonText: "確定"
              });
            } finally {
              this.isLoading = false;
            }
          }
        });
    },
    handleCreate() {
      this.$router.push({
        name: "SuiteCreate",
      });
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    canDelete(item) {
      return item.is_enabled === 0
    }
  },
};
</script>
